/**
 * 사내 방송 — **무엇으로 틀 것인가** (2026-09-09 · 육안 지적에서 드러났다).
 *
 * ⚠⚠⚠ **YouTube 링크는 `<video>` 로 «절대» 안 돈다.** 운영에 등록돼 있던 두 편이
 *   `https://www.youtube.com/watch?v=…` 와 `https://youtu.be/…` 였는데, 위젯은 그 주소를
 *   그대로 `<video src>` 에 물리고 있었다 — 그것은 영상이 아니라 **HTML 문서**라 아무것도
 *   재생되지 않는다. 「자동 재생이 안 된다」의 밑에는 **「누르는 재생도 안 됐다」**가 있었다.
 *   통합설정이 `youtube.com` · `youtu.be` 를 허용 도메인 기본값으로 두고 있으니
 *   **링크 영상은 처음부터 이 서비스가 하려던 일**이다.
 *
 * ★★★ 그래서 주소를 보고 **그릇을 고른다**: 파일·직접 미디어 주소는 `<video>`, 끼워 넣을 수
 *     있는 곳(YouTube · Vimeo)은 `<iframe>`, 그 밖에는 **못 튼다고 말한다**(빈 검은 상자를
 *     보여 주고 「고장 났나」 하게 두지 않는다 — 대신 원본으로 가는 길을 준다).
 *
 * ★★ 자동 재생은 어느 그릇이든 **음소거**다 — 브라우저가 소리 나는 자동 재생을 막는다.
 * ★ 이어보기 지점은 **시작 시각**으로만 넘긴다. 끼워 넣은 재생기는 「지금 몇 초」를 알려 주지
 *   않으므로(그러려면 남의 스크립트를 얹어야 한다) **새 지점을 적지는 못한다** — 화면이 그
 *   사실을 적는다.
 */

export type PlayTarget =
  /** 브라우저가 직접 트는 미디어 — 파일(서명 주소)이나 .mp4 같은 직접 주소. */
  | { kind: 'video'; src: string }
  /** 남의 재생기를 끼워 넣는다 — YouTube · Vimeo. */
  | { kind: 'embed'; src: string; provider: 'youtube' | 'vimeo' }
  /** 못 튼다 — 원본으로 보낸다. */
  | { kind: 'link'; href: string; host: string };

export interface PlayOptions {
  /** 누르지 않고 시작하는가 — 그러면 반드시 음소거다. */
  autoplay?: boolean;
  muted?: boolean;
  /** 이어볼 지점(초). 0 이면 처음부터. */
  startSec?: number;
}

const DIRECT_MEDIA = /\.(mp4|webm|ogv|ogg|m4v|mov|m3u8)(\?|#|$)/i;

/** YouTube 의 영상 id — watch?v= · youtu.be/ · /embed/ · /shorts/ 넷을 본다. */
export function youTubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, '');
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    return id || null;
  }
  if (host !== 'youtube.com' && host !== 'm.youtube.com' && host !== 'music.youtube.com') return null;
  const v = url.searchParams.get('v');
  if (v) return v;
  const m = /^\/(embed|shorts|live|v)\/([^/?#]+)/.exec(url.pathname);
  return m?.[2] ?? null;
}

/** Vimeo 의 영상 번호. */
export function vimeoId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, '');
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
  const m = /(\d{6,})/.exec(url.pathname);
  return m?.[1] ?? null;
}

/**
 * 이 영상을 무엇으로 틀 것인가.
 *
 * @param kind  서버가 말해 준 갈래 — `FILE` 이면 서명 주소라 늘 `<video>` 다.
 */
export function playTarget(url: string, kind: 'FILE' | 'URL', opts: PlayOptions = {}): PlayTarget {
  const { autoplay = false, muted = false, startSec = 0 } = opts;
  if (kind === 'FILE') return { kind: 'video', src: url };

  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return { kind: 'link', href: url, host: '' };
  }

  const yt = youTubeId(u);
  if (yt) {
    const q = new URLSearchParams({ rel: '0', playsinline: '1', modestbranding: '1' });
    if (autoplay) q.set('autoplay', '1');
    if (muted || autoplay) q.set('mute', '1');   // ★ 소리 나는 자동 재생은 막힌다.
    if (startSec > 0) q.set('start', String(Math.floor(startSec)));
    return { kind: 'embed', provider: 'youtube', src: `https://www.youtube.com/embed/${yt}?${q}` };
  }

  const vm = vimeoId(u);
  if (vm) {
    const q = new URLSearchParams();
    if (autoplay) q.set('autoplay', '1');
    if (muted || autoplay) q.set('muted', '1');
    const hash = startSec > 0 ? `#t=${Math.floor(startSec)}s` : '';
    return { kind: 'embed', provider: 'vimeo', src: `https://player.vimeo.com/video/${vm}?${q}${hash}` };
  }

  if (DIRECT_MEDIA.test(u.pathname) || DIRECT_MEDIA.test(u.href)) {
    return { kind: 'video', src: u.href };
  }
  return { kind: 'link', href: u.href, host: u.hostname.replace(/^www\./, '') };
}
